import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  AddCircleOutline,
  CreditCard,
  CurrencyExchange,
  DeleteOutline,
  Person,
  RemoveCircleOutline,
  Restaurant
} from "@mui/icons-material";
import { EditSkeleton } from "components/admin/admin.overlays";
import BaseSearch from "components/shared/baseSearch/BaseSearch";
import MarketplaceAllergyModal from "components/shared/marketplaceAllergyModal/MarketplaceAllergyModal";
import MarketplaceSubItemModal from "components/shared/martketplaceSubitemModal/MarketplaceSubItemModal";
import React, { useEffect, useMemo, useState } from "react";
import ConfirmModal from "components/shared/confirmModal/ConfirmModal";

export default function MarketplaceMenuSection({
  menuData,
  menuDataLoading,
  isSubmitting,
  showChargeResident,
  onOrderSubmit,
  marketplaceId,
}) {
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [selectedItems, setSelectedItems] = useState({});
  const [subItemModalOpen, setSubItemModalOpen] = useState(false);
  const [activeMenuItem, setActiveMenuItem] = useState(null);
  const [allergyModalOpen, setAllergyModalOpen] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  useEffect(() => {
    setSearchValue("");
    setSelectedItems({});
    setSelectedFilter(null);
  }, [marketplaceId]);

  const filteredItems = useMemo(() => {
    let items = menuData?.bistroMenuItems || [];
    if (searchValue)
      items = items.filter((i) =>
        i.name?.toLowerCase().includes(searchValue.toLowerCase())
      );
    if (selectedFilter) {
      if (selectedFilter.subCategoryId) {
        items = items.filter(
          (i) => i.bistroMenuSubCategoryId === selectedFilter.subCategoryId
        );
      } else {
        items = items.filter(
          (i) => i.bistroMenuCategoryId === selectedFilter.categoryId
        );
      }
    }
    return items;
  }, [menuData?.bistroMenuItems, searchValue, selectedFilter]);

  const orderTotal = useMemo(
    () =>
      Object.values(selectedItems).reduce((sum, item) => {
        const subTotal = Object.values(item.subItems || {}).reduce(
          (s, sub) => s + (sub.price ?? 0) * sub.quantity,
          0
        );
        return sum + (item.price ?? 0) * item.quantity + subTotal;
      }, 0),
    [selectedItems]
  );

  const handleItemClick = (item) => {
    if (!selectedItems[item.id]) {
      setSelectedItems((prev) => ({
        ...prev,
        [item.id]: { ...item, quantity: 1, note: "", subItems: {} },
      }));
    }
    if (item?.subItems?.length) {
      setActiveMenuItem(item);
      setSubItemModalOpen(true);
    }
  };

  const handleAllergyClick = (item) => {
    setActiveMenuItem(item);
    setAllergyModalOpen(true);
  };

  const handleSubItemModalClose = (itemId, subItems, note) => {
    setSelectedItems((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], subItems, note },
    }));
    setSubItemModalOpen(false);
  };

  const handleQuantityChange = (itemId, delta) => {
    setSelectedItems((prev) => {
      const current = prev[itemId];
      if (!current) return prev;
      const newQty = current.quantity + delta;
      if (newQty <= 0) {
        // eslint-disable-next-line no-unused-vars
        const { [itemId]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [itemId]: { ...current, quantity: newQty } };
    });
  };

  const handleSubmit = async (billingTypeId) => {
    if (!Object.keys(selectedItems).length || isSubmitting) return;
    const success = await onOrderSubmit(billingTypeId, selectedItems, orderTotal);
    if (success) setSelectedItems({});
  };

  if (menuDataLoading) {
    return (
      <Grid size={{ xs: 12 }}>
        <EditSkeleton />
      </Grid>
    );
  }

  return (
    <>
      <Grid
        container
        size={{ xs: 12 }}
        spacing={1}
        sx={{ alignItems: "flex-start", margin: 0, padding: 0 }}
      >
        {/* Category sidebar */}
        <Grid size={{ xs: 3 }}>
          <Box>
            <Box
              display="flex"
              justifyContent="space-between"
              onClick={() => setSelectedFilter(null)}
              sx={{
                marginTop: ".75rem",
                paddingY: ".5rem",
                paddingRight: "1.5rem",
                cursor: "pointer",
                backgroundColor: !selectedFilter ? "var(--tealColor)" : "#eff4f6",
                color: !selectedFilter ? "#FFF" : "#000",
                borderRadius: "var(--borderRadius)"
              }}
            >
              <Typography fontWeight="bold" ml=".25rem">
                All Categories
              </Typography>
            </Box>
            {menuData?.bistroMenuCategories?.map((cat) => (
              <React.Fragment key={cat.id}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  onClick={() =>
                    setSelectedFilter({ categoryName: cat.name, subCategoryName: null, categoryId: cat.id, subCategoryId: null })
                  }
                  sx={{
                    marginTop: ".125rem",
                    paddingY: ".5rem",
                    paddingRight: "1.5rem",
                    cursor: "pointer",
                    borderRadius: "var(--borderRadius)",
                    backgroundColor:
                      selectedFilter?.categoryId === cat.id &&
                      !selectedFilter?.subCategoryId
                        ? "var(--tealColor)"
                        : "#eff4f6",
                    color:
                      selectedFilter?.categoryId === cat.id &&
                      !selectedFilter?.subCategoryId
                        ? "#FFF"
                        : "#000",
                  }}
                >
                  <Typography variant="subtitle1" ml=".25rem">
                    {cat.name}
                  </Typography>
                </Box>
                {selectedFilter?.categoryId == cat.id && cat?.subCategoryDetail?.map((sub) => (
                  <Box
                    key={sub.id}
                    display="flex"
                    justifyContent="space-between"
                    onClick={() =>
                      setSelectedFilter({
                        categoryName: cat.name,
                        categoryId: cat.id,
                        subCategoryName: sub.name,
                        subCategoryId: sub.id
                      })
                    }
                    sx={{
                      paddingY: ".25rem",
                      paddingX: "1.5rem",
                      cursor: "pointer",
                      borderRadius: "var(--borderRadius)",
                      backgroundColor:
                        selectedFilter?.subCategoryId === sub.id
                          ? "var(--tealColor)"
                          : "transparent",
                      color:
                        selectedFilter?.subCategoryId === sub.id ? "#FFF" : "#000",
                    }}
                  >
                    <Typography variant="subtitle2">
                      <i>{sub.name}</i>
                    </Typography>
                  </Box>
                ))}
              </React.Fragment>
            ))}
          </Box>
        </Grid>

        {/* Item list */}
        <Grid size={{ xs: 5.5 }}>
          <BaseSearch
            value={searchValue}
            setSearchValue={setSearchValue}
            placeholder="Search Items..."
            sx={{ width: "250px" }}
          />
          <Box sx={{ height: "65vh", overflowY: "auto", mt: 1 }}>
            {filteredItems.map((item) => {
              const isSelected = !!selectedItems[item.id];
              const hasNutrition =
                !!item?.kCal ||
                !!item?.fat ||
                !!item?.carbohydrates ||
                !!item?.protein ||
                !!item?.allergies?.length;
              return (
                <Box
                  key={item.id}
                  onClick={() => handleItemClick(item)}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: ".5rem 1rem",
                    minHeight: "70px",
                    mb: ".25rem",
                    borderRadius: ".5rem",
                    cursor: "pointer",
                    backgroundColor: isSelected ? "#cee2e3" : "#fff",
                    border: "1px solid rgba(128,128,128,.3)",
                    "&:hover": {
                      backgroundColor: isSelected ? "#bdd6d7" : "#f5f5f5",
                    },
                  }}
                >
                  <Box>
                    <Typography fontWeight="bold" fontSize="1.1rem">
                      {item.name}{" "}
                      {hasNutrition && (
                        <Restaurant
                          sx={{
                            fontSize: "1.55rem",
                            verticalAlign: "-6px",
                            color: item.hasAllergy ? "var(--redColor)" : "inherit",
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleAllergyClick(item);
                          }}
                        />
                      )}
                    </Typography>
                    {!!item?.description && (
                      <Box
                        sx={{ minWidth: isSelected ? "100%%" : "85%" }}
                      >
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ 
                            display: "block", 
                            lineHeight: "1.05 !important",
                          }}
                        >
                          {item.description}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  {isSelected ? (
                    <Box>
                      <Box
                        display="flex"
                        alignItems="center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleQuantityChange(item.id, -1)}
                        >
                          <RemoveCircleOutline sx={{ fontSize: "1.75rem" }} />
                        </IconButton>
                        <Typography
                          sx={{ minWidth: "1.5rem", textAlign: "center", fontWeight: "bold" }}
                        >
                          {selectedItems[item.id].quantity}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleQuantityChange(item.id, 1)}
                          disabled={selectedItems[item.id].quantity >= (item?.stockCount ?? 99)}
                        >
                          <AddCircleOutline sx={{ fontSize: "1.75rem" }} />
                        </IconButton>
                      </Box>
                      {(item.autoOutOfStock && selectedItems[item.id].quantity >= item?.stockCount) && (
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Typography
                            variant="caption"
                            color="error.main"
                          >
                            Stock Limit
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    item.price != null && (
                      <Typography fontSize=".9rem" color="text.secondary">
                        ${item.price.toFixed(2)}
                      </Typography>
                    )
                  )}
                </Box>
              );
            })}
          </Box>
        </Grid>

        {/* Order panel */}
        <Grid size={{ xs: 3.5 }} py={1}>
          <Box
            sx={{
              border: "1px solid rgba(128,128,128,.3)",
              borderRadius: ".5rem",
              minHeight: "40vh",
              maxHeight: "62.5vh",
              display: "flex",
              flexDirection: "column",
              overflow: "auto",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: ".75rem 1rem",
                backgroundColor: "var(--tealColor)",
                color: "#fff",
                borderRadius: ".5rem .5rem 0 0",
              }}
            >
              <Typography fontWeight="bold" letterSpacing=".05rem" fontSize="1rem">
                CURRENT ORDER
              </Typography>
              <IconButton
                size="small"
                disabled={!Object.keys(selectedItems).length}
                sx={{
                  color: "#fff",
                  backgroundColor: "var(--redColor)",
                  borderRadius: "var(--borderRadius)",
                  "&:hover": {
                    color: "#FFF",
                    backgroundColor: "var(--redColor)"
                  },
                  "&:disabled": {
                    backgroundColor: "lightgray",
                    color: "#000"
                  }
                }}
                onClick={() => setOpenDeleteModal(!!Object.keys(selectedItems).length)}
              >
                <DeleteOutline />
              </IconButton>
            </Box>

            <Box sx={{ flex: 1, overflowY: "auto", padding: ".75rem 1rem" }}>
              {Object.values(selectedItems).map((item) => (
                <Box key={item.id} mb={1}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography fontSize=".9rem" fontWeight="bold">
                      {item.name} ×{item.quantity}
                    </Typography>
                    {item.price != null && (
                      <Typography fontSize=".9rem">
                        ${(item.price * item.quantity).toFixed(2)}
                      </Typography>
                    )}
                  </Box>
                  {Object.values(item.subItems || {}).map((sub) => (
                    <Box
                      key={sub.id}
                      display="flex"
                      justifyContent="space-between"
                      pl={1}
                    >
                      <Typography fontSize=".8rem" color="text.secondary">
                        + {sub.name} ×{sub.quantity}
                      </Typography>
                      <Typography fontSize=".8rem" color="text.secondary">
                        ${((sub.price ?? 0) * sub.quantity).toFixed(2)}
                      </Typography>
                    </Box>
                  ))}
                  {item.note && (
                    <Typography
                      fontSize=".8rem"
                      color="text.secondary"
                      pl={1}
                      fontStyle="italic"
                    >
                      Note: {item.note}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>

            <Box sx={{ padding: "0 1rem 1rem" }}>
              <Divider sx={{ mb: 1 }} />
              <Box display="flex" justifyContent="space-between" mb={1.5}>
                <Typography fontWeight="bold">TOTAL</Typography>
                <Typography fontWeight="bold">${orderTotal.toFixed(2)}</Typography>
              </Box>
              {/*eslint-disable-next-line no-undef */}
              {process.env.REACT_APP_BASE === 'dev' && (
                <Button
                  variant="contained"
                  fullWidth
                  disabled={!Object.keys(selectedItems).length || isSubmitting}
                  onClick={() => handleSubmit(1)}
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <CreditCard />
                    )
                  }
                  sx={{ mb: 1, fontWeight: "bold", fontSize: ".75rem" }}
                >
                  PAY WITH STRIPE
                </Button>
              )}
              {showChargeResident && (
                <Button
                  variant="outlined"
                  fullWidth
                  disabled={!Object.keys(selectedItems).length || isSubmitting}
                  onClick={() => handleSubmit(2)}
                  sx={{
                    mb: 1,
                    fontWeight: "bold",
                    fontSize: ".75rem",
                    borderColor: "var(--tealColor)",
                    color: "var(--tealColor)",
                  }}
                  startIcon={<Person />}
                >
                  CHARGE RESIDENT ACCOUNT
                </Button>
              )}
              <Button
                variant="outlined"
                fullWidth
                disabled={!Object.keys(selectedItems).length || isSubmitting}
                onClick={() => handleSubmit(3)}
                sx={{
                  fontWeight: "bold",
                  fontSize: ".75rem",
                  borderColor: "var(--tealColor)",
                  color: "var(--tealColor)",
                }}
                startIcon={<CurrencyExchange />}
              >
                EXTERNAL PAYMENT
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {subItemModalOpen && (
        <MarketplaceSubItemModal
          open={subItemModalOpen}
          selectedItem={activeMenuItem}
          currentSubItems={
            activeMenuItem ? (selectedItems[activeMenuItem.id]?.subItems ?? {}) : {}
          }
          note={activeMenuItem ? (selectedItems[activeMenuItem.id]?.note ?? "") : ""}
          handleClose={handleSubItemModalClose}
        />
      )}
      {allergyModalOpen && (
        <MarketplaceAllergyModal
          open={allergyModalOpen}
          selectedItem={activeMenuItem}
          handleClose={() => setAllergyModalOpen(false)}
        />
      )}
      {openDeleteModal && (
        <ConfirmModal 
          open={openDeleteModal}
          title={"Delete Order Items"}
          isWarningModal={true}
          cancelText="Cancel"
          confirmText="Delete"
          entityName={"all items from the cart"}
          entityDesc={"all items from your cart"}
          handleClose={(isConfirmed) => {
            if (isConfirmed) {
              setSelectedItems([]);
            }
            setOpenDeleteModal(false);
          }}
        />
      )}
    </>
  );
}
